import { useState, useCallback, useEffect } from 'react';

import { usePlaidLink } from 'react-plaid-link';
import { useAuthContext } from 'src/auth/hooks';
import useLinkItems from 'src/contexts/useLinkItems';
import fetchData from 'src/services/api';

export default function useLink() {
  const { user, refreshAccount } = useAuthContext();
  const items = useLinkItems();

  const [account, setAccount] = useState(null);
  const [linkToken, setLinkToken] = useState(null);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const generateToken = async (acc) => {
    setAccount(acc);
    setLoading(true);
    const itemId = acc?.plaid_item_id;

    const existingToken = items?.find((e) => e.id === itemId)?.plaid_access_token;
    const res = await fetchData('/api/providers/plaid/link-token', {
      method: 'POST',
      body: {
        userId: user.uid,
        token: existingToken,
      },
    }).catch((err) => {
      setError(err.message);
      setSuccess(false);
    });

    setLinkToken(res.link_token);
  };

  const onSuccess = useCallback(
    async (publicToken, metadata) => {
      setLoading(true);
      const body = {
        publicToken,
        provider: publicToken,
        institutionId: metadata.institution.institution_id,
        userId: user.uid,
      };

      await fetchData('/api/providers/plaid/items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      })
        .then(() => {
          setSuccess(true);
          setError(null);
          refreshAccount();
        })
        .catch((err) => {
          setError(err.message);
          setSuccess(false);
          console.error('error adding plaid account', err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [refreshAccount, user.uid]
  );

  const onExit = useCallback(() => {
    setLinkToken(null);
    setLoading(false);
  }, []);

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  useEffect(() => {
    if (ready && linkToken) {
      open();
    }
  }, [linkToken, ready, open]);

  const statusForLinkItem = (id) => items?.find((e) => e.id === id)?.status;

  return {
    generateToken,
    statusForLinkItem,
    linkToken,
    ready,
    loading,
    success,
    error,
    account,
  };
}
